import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Utils from 'common/services/Utils';
import { CostCenterAggregationDashboardDto } from 'api/dashboard/dtos/CostCenterAggregationDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useInfiniteReveal from '../components/useInfiniteReveal';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import KpiHeader from '../components/KpiHeader';
import { getPreviousRange, PreviousRange } from '../utils/getPreviousRange';
import styles from '../topMaintenanceCosts/TopMaintenanceCosts.module.scss';
import layoutStyles from './CostCenterLayout.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

const SCALE_COLORS = [
	'#025a7a',
	'#038cb6',
	'#02c7c7',
	'#6de0e0',
	'#b8f0f0',
];

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const CostCenterExpenses = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<CostCenterAggregationDashboardDto[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [previousTotal, setPreviousTotal] = useState<number | null>(null);
	const [previousRange, setPreviousRange] = useState<PreviousRange | null>(null);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('costCenterExpenses');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			setPreviousTotal(null);
			setPreviousRange(null);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo, ...vehicleScope };
				const result = await DashboardService.getCostCenterExpenses(criteria);
				if (!cancelled) {
					setData(result || []);
				}
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}

			try {
				const range = getPreviousRange(dateFrom, dateTo);
				const prevCriteria = props.filters
					? { ...props.filters, dateFrom: range.prevFrom, dateTo: range.prevTo }
					: { dateFrom: range.prevFrom, dateTo: range.prevTo, ...vehicleScope };
				const previous = await DashboardService.getCostCenterExpenses(prevCriteria);
				if (!cancelled) {
					setPreviousTotal((previous || []).reduce((s, x) => s + x.totalCost, 0));
					setPreviousRange(range);
				}
			} catch {
				if (!cancelled) {
					setPreviousTotal(null);
					setPreviousRange(null);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	const items = data || [];
	const maxCost = Math.max(...items.map(d => d.totalCost), 0);
	const grandTotal = items.reduce((sum, it) => sum + it.totalCost, 0);
	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(items, PAGE_SIZE);

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.cost_center_expenses') as string}
			subTitleTooltip={t('dashboard.cost_center_expenses_tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}>
					<LoadingSpinner />
				</div>
			) : items.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={Utils.formatCurrency(grandTotal)}
						subLabel={t('dashboard.total_cost' as any) as string}
						currentTotal={grandTotal}
						previousTotal={previousTotal}
						previousRange={previousRange}
					/>
					<div className={styles.list}>
					<ReactTooltip effect="solid" />
					{visibleItems.map((item, index) => {
						const barWidth = maxCost > 0 ? (item.totalCost / maxCost) * 100 : 0;
						const color = SCALE_COLORS[Math.min(index, SCALE_COLORS.length - 1)];
						return (
							<div
								key={item.geofenceId}
								className={styles.row}
								onClick={() => window.open(`/geofences/details/${item.geofenceId}`, '_blank')}
							>
								<span className={`${styles.plate} ${layoutStyles.plateWide}`} data-tip={item.name}>{item.name}</span>
								<div className={styles.barTrack}>
									<div
										className={styles.barFill}
										style={{ width: `${barWidth}%`, background: color }}
									/>
								</div>
								<span className={`${styles.cost} ${index === 0 ? styles.costFirst : ''}`}>
									{Utils.formatCurrency(item.totalCost)}
								</span>
							</div>
						);
					})}
					{hasMore && <div ref={sentinelRef} className={infiniteStyles.sentinel} />}
					</div>
				</>
			)}
		</Box>
	);
};

export default CostCenterExpenses;
